/**
 * Remoção de fundo da assinatura (só heurísticas no cliente, sem IA).
 * 1) Flood pela cor média das bordas.
 * 2) Flood por luminância a partir das bordas (vários limiares — fundo cinza, bordas com tinta).
 * 3) Papel claro global (vários limiares; escolhe o que mais remove fundo sem perder traço).
 * 4) Expansão: transparência “invade” pixels claros colados ao recorte.
 * 5) Se inválido ou traço quase perdido: mantém o original.
 */

const DEFAULT_TOLERANCE = 42;
const DEFAULT_MAX_DIMENSION = 1400;
const HEURISTIC_MIN_TRANSPARENT_RATIO = 0.05;
const INK_MAX_LUMINANCE = 145;
const MIN_INK_RETENTION = 0.35;
const MIN_OUTPUT_INK_FRACTION = 0.0025;

function colorDistance(r1, g1, b1, r2, g2, b2) {
  return Math.hypot(r1 - r2, g1 - g2, b1 - b2);
}

function luminance(r, g, b) {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * @param {ImageData} imageData
 * @param {number} tolerance distância RGB máxima em relação à média das bordas
 * @returns {ImageData}
 */
export function removeBackgroundFromImageData(imageData, tolerance = DEFAULT_TOLERANCE) {
  const { width, height, data } = imageData;
  const len = width * height;
  const visited = new Uint8Array(len);
  const out = new Uint8ClampedArray(data.length);
  out.set(data);

  let rSum = 0;
  let gSum = 0;
  let bSum = 0;
  let count = 0;

  const sampleEdge = (x, y) => {
    const i = (y * width + x) * 4;
    rSum += data[i];
    gSum += data[i + 1];
    bSum += data[i + 2];
    count += 1;
  };

  for (let x = 0; x < width; x += 1) {
    sampleEdge(x, 0);
    sampleEdge(x, height - 1);
  }
  for (let y = 0; y < height; y += 1) {
    sampleEdge(0, y);
    sampleEdge(width - 1, y);
  }

  const br = rSum / count;
  const bg = gSum / count;
  const bb = bSum / count;

  const distAt = (pixelIndex) => {
    const r = data[pixelIndex];
    const g = data[pixelIndex + 1];
    const b = data[pixelIndex + 2];
    return colorDistance(r, g, b, br, bg, bb);
  };

  const queue = [];
  const trySeed = (x, y) => {
    if (x < 0 || x >= width || y < 0 || y >= height) return;
    const p = y * width + x;
    if (visited[p]) return;
    const i = p * 4;
    if (distAt(i) > tolerance) return;
    visited[p] = 1;
    queue.push(x, y);
  };

  for (let x = 0; x < width; x += 1) {
    trySeed(x, 0);
    trySeed(x, height - 1);
  }
  for (let y = 0; y < height; y += 1) {
    trySeed(0, y);
    trySeed(width - 1, y);
  }

  let q = 0;
  while (q < queue.length) {
    const x = queue[q++];
    const y = queue[q++];
    const p = y * width + x;
    const i = p * 4;
    out[i + 3] = 0;

    const neighbors = [
      [x + 1, y],
      [x - 1, y],
      [x, y + 1],
      [x, y - 1],
    ];
    for (const [nx, ny] of neighbors) {
      if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
      const np = ny * width + nx;
      if (visited[np]) continue;
      const ni = np * 4;
      if (distAt(ni) > tolerance) continue;
      visited[np] = 1;
      queue.push(nx, ny);
    }
  }

  return new ImageData(out, width, height);
}

/**
 * Flood a partir das bordas: só atravessa pixels com luminância >= limiar (papel claro).
 * @param {ImageData} imageData
 * @param {number} paperMinLuminance típ. 195–210
 */
export function removeBackgroundLuminanceFlood(imageData, paperMinLuminance = 200) {
  const { width, height, data } = imageData;
  const visited = new Uint8Array(width * height);
  const out = new Uint8ClampedArray(data.length);
  out.set(data);

  const lumAt = (i) => luminance(data[i], data[i + 1], data[i + 2]);

  const queue = [];
  const trySeed = (x, y) => {
    if (x < 0 || x >= width || y < 0 || y >= height) return;
    const p = y * width + x;
    if (visited[p]) return;
    const i = p * 4;
    if (lumAt(i) < paperMinLuminance) return;
    visited[p] = 1;
    queue.push(x, y);
  };

  for (let x = 0; x < width; x += 1) {
    trySeed(x, 0);
    trySeed(x, height - 1);
  }
  for (let y = 0; y < height; y += 1) {
    trySeed(0, y);
    trySeed(width - 1, y);
  }

  let q = 0;
  while (q < queue.length) {
    const x = queue[q++];
    const y = queue[q++];
    const p = y * width + x;
    const i = p * 4;
    out[i + 3] = 0;

    const neighbors = [
      [x + 1, y],
      [x - 1, y],
      [x, y + 1],
      [x, y - 1],
    ];
    for (const [nx, ny] of neighbors) {
      if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
      const np = ny * width + nx;
      if (visited[np]) continue;
      const ni = np * 4;
      if (lumAt(ni) < paperMinLuminance) continue;
      visited[np] = 1;
      queue.push(nx, ny);
    }
  }

  return new ImageData(out, width, height);
}

/**
 * Remove qualquer pixel com luminância >= limiar (fundo cinza claro, branco, ilhas internas).
 * Usa só o RGB do original; tinta escura permanece opaca.
 */
export function removeBackgroundGlobalLuminance(imageData, paperMinLuminance = 178) {
  const { width, height, data } = imageData;
  const out = new Uint8ClampedArray(data.length);
  out.set(data);
  for (let i = 0; i < data.length; i += 4) {
    if (luminance(data[i], data[i + 1], data[i + 2]) >= paperMinLuminance) {
      out[i + 3] = 0;
    }
  }
  return new ImageData(out, width, height);
}

/**
 * A partir de pixels já transparentes, torna transparentes vizinhos opacos com luminância >= minL
 * (liga fundo cinza residual ao recorte). RGB lido do original.
 */
export function expandTransparencyIntoLightPaper(originalImageData, processedImageData, minLuminance = 200) {
  const { width, height } = processedImageData;
  const od = originalImageData.data;
  const out = new Uint8ClampedArray(processedImageData.data);
  const lumAtOrig = (idx) => luminance(od[idx], od[idx + 1], od[idx + 2]);

  const queue = [];
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      if (out[i + 3] < 16) queue.push(x, y);
    }
  }

  let q = 0;
  while (q < queue.length) {
    const x = queue[q++];
    const y = queue[q++];
    const neighbors = [
      [x + 1, y],
      [x - 1, y],
      [x, y + 1],
      [x, y - 1],
    ];
    for (const [nx, ny] of neighbors) {
      if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
      const ni = (ny * width + nx) * 4;
      if (out[ni + 3] < 16) continue;
      if (lumAtOrig(ni) < minLuminance) continue;
      out[ni + 3] = 0;
      queue.push(nx, ny);
    }
  }

  return new ImageData(out, width, height);
}

/** Fração de pixels da imagem que parecem tinta. */
function inkFractionFullImage(imageData) {
  const { width, height, data } = imageData;
  const n = width * height;
  let ink = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (luminance(data[i], data[i + 1], data[i + 2]) < INK_MAX_LUMINANCE) ink += 1;
  }
  return ink / n;
}

/** Fração da área total: pixels opacos no resultado que ainda parecem tinta. */
function inkFractionInOpaqueOutput(imageData) {
  const { width, height, data } = imageData;
  let ink = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 16) continue;
    if (luminance(data[i], data[i + 1], data[i + 2]) < INK_MAX_LUMINANCE) ink += 1;
  }
  return ink / (width * height);
}

async function imageDataToPngBlob(imageData) {
  const canvas = document.createElement("canvas");
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D não disponível");
  ctx.putImageData(imageData, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob falhou"))), "image/png");
  });
}

async function blobTransparentPixelRatio(blob) {
  const bmp = await createImageBitmap(blob);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return 0;
    ctx.drawImage(bmp, 0, 0);
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let transparent = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 16) transparent += 1;
    }
    return transparent / (data.length / 4);
  } finally {
    bmp.close();
  }
}

function isProcessedSignatureAcceptable(processed, inputInkFrac) {
  const outInk = inkFractionInOpaqueOutput(processed);
  const minByRetention = inputInkFrac * MIN_INK_RETENTION;
  const floor = Math.max(MIN_OUTPUT_INK_FRACTION, inputInkFrac > 0.008 ? minByRetention : MIN_OUTPUT_INK_FRACTION);
  return outInk >= floor;
}

/** Luminância média (RGB original) dos pixels ainda opacos no resultado — alto ⇒ muito fundo/cinza restante. */
function meanLuminanceOpaqueOriginal(processed, originalImageData) {
  const pd = processed.data;
  const od = originalImageData.data;
  let sum = 0;
  let count = 0;
  for (let i = 0; i < pd.length; i += 4) {
    if (pd[i + 3] < 16) continue;
    sum += luminance(od[i], od[i + 1], od[i + 2]);
    count += 1;
  }
  return count > 0 ? sum / count : 0;
}

async function fileToScaledImageData(file, maxDimension) {
  const bmp = await createImageBitmap(file);
  try {
    let w = bmp.width;
    let h = bmp.height;
    const scale = Math.min(1, maxDimension / Math.max(w, h));
    w = Math.round(w * scale);
    h = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("Canvas 2D não disponível");
    ctx.drawImage(bmp, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h);
  } finally {
    bmp.close();
  }
}

/**
 * Heurística → PNG transparente (uma passagem: média de bordas).
 */
export async function signatureImageToHeuristicPngBlob(file, options = {}) {
  const tolerance = options.tolerance ?? DEFAULT_TOLERANCE;
  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const imageData = await fileToScaledImageData(file, maxDimension);
  const processed = removeBackgroundFromImageData(imageData, tolerance);
  return imageDataToPngBlob(processed);
}

/**
 * @param {{ tolerance?: number; maxDimension?: number; onProcessProgress?: (p: { percent: number; label: string }) => void }} [options]
 * @returns {Promise<{ blob: Blob; transparencyApplied: boolean }>}
 */
export async function signatureImageToTransparentPngBlobWithMeta(file, options = {}) {
  const report = (percent, label) => options.onProcessProgress?.({ percent, label });
  const tolerance = options.tolerance ?? DEFAULT_TOLERANCE;
  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;

  report(8, "Carregando imagem…");
  const imageData = await fileToScaledImageData(file, maxDimension);
  const inputInkFrac = inkFractionFullImage(imageData);

  report(22, "Removendo fundo (cor das bordas)…");
  let processed = removeBackgroundFromImageData(imageData, tolerance);
  let blob = await imageDataToPngBlob(processed);
  let transparentRatio = await blobTransparentPixelRatio(blob);

  const LUM_EDGE_LEVELS = [200, 188, 175, 165];
  for (const L of LUM_EDGE_LEVELS) {
    if (transparentRatio >= HEURISTIC_MIN_TRANSPARENT_RATIO) break;
    report(36, `Removendo fundo (bordas, clareza ${L})…`);
    processed = removeBackgroundLuminanceFlood(imageData, L);
    blob = await imageDataToPngBlob(processed);
    transparentRatio = await blobTransparentPixelRatio(blob);
  }

  const tryGlobalAt = async (lum) => {
    const candidate = removeBackgroundGlobalLuminance(imageData, lum);
    if (!isProcessedSignatureAcceptable(candidate, inputInkFrac)) return null;
    const b = await imageDataToPngBlob(candidate);
    const r = await blobTransparentPixelRatio(b);
    return { processed: candidate, blob: b, transparentRatio: r };
  };

  const meanPaperL = meanLuminanceOpaqueOriginal(processed, imageData);
  const needsGlobal =
    transparentRatio < 0.5 ||
    meanPaperL >= 148 ||
    transparentRatio < HEURISTIC_MIN_TRANSPARENT_RATIO * 0.45;

  if (needsGlobal) {
    report(52, "Removendo fundo cinza/branco (global)…");
    let best = { processed, blob, transparentRatio };
    for (const lum of [182, 172, 165, 158]) {
      const g = await tryGlobalAt(lum);
      if (g && g.transparentRatio > best.transparentRatio) best = g;
    }
    processed = best.processed;
    blob = best.blob;
    transparentRatio = best.transparentRatio;
  }

  report(62, "Refinando transparência…");
  processed = expandTransparencyIntoLightPaper(imageData, processed, 192);
  blob = await imageDataToPngBlob(processed);
  transparentRatio = await blobTransparentPixelRatio(blob);

  if (transparentRatio < HEURISTIC_MIN_TRANSPARENT_RATIO * 0.4) {
    report(68, "Fundo não confiável — mantendo original");
    const origBlob = await file.arrayBuffer().then((buf) => new Blob([buf], { type: file.type || "image/png" }));
    return { blob: origBlob, transparencyApplied: false };
  }

  if (!isProcessedSignatureAcceptable(processed, inputInkFrac)) {
    report(72, "Traço quase perdido — mantendo original");
    const origBlob = await file.arrayBuffer().then((buf) => new Blob([buf], { type: file.type || "image/png" }));
    return { blob: origBlob, transparencyApplied: false };
  }

  report(78, "Fundo removido");
  return { blob, transparencyApplied: true };
}

export async function signatureImageToTransparentPngBlob(file, options = {}) {
  const { blob } = await signatureImageToTransparentPngBlobWithMeta(file, options);
  return blob;
}

/**
 * @returns {Promise<{ file: File; transparencyApplied: boolean }>}
 */
export async function prepareAssinaturaFileForUploadWithMeta(file, options = {}) {
  const type = (file.type || "").toLowerCase();
  if (!/^image\/(png|jpeg|jpg)$/.test(type)) {
    return { file, transparencyApplied: false };
  }
  try {
    options.onProcessProgress?.({ percent: 2, label: "Preparando assinatura…" });
    const { blob, transparencyApplied } = await signatureImageToTransparentPngBlobWithMeta(file, {
      tolerance: options.tolerance,
      maxDimension: options.maxDimension,
      onProcessProgress: options.onProcessProgress,
    });
    const base = (file.name || "assinatura").replace(/\.[^.]+$/i, "") || "assinatura";
    const outName = transparencyApplied ? `${base}.png` : file.name;
    const outType = transparencyApplied ? "image/png" : file.type || "application/octet-stream";
    return { file: new File([blob], outName, { type: outType }), transparencyApplied };
  } catch (e) {
    console.warn("[assinatura] Processamento ignorado, usando arquivo original:", e);
    return { file, transparencyApplied: false };
  }
}

export async function prepareAssinaturaFileForUpload(file, options = {}) {
  const { file: out } = await prepareAssinaturaFileForUploadWithMeta(file, options);
  return out;
}
